import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, User, Shield, Info, Link, ExternalLink, Save, Eye, EyeOff, Users, Search, Trash2, AlertTriangle, Inbox, Check, X as XIcon, Camera, RefreshCw } from 'lucide-react';
import { getPrivacySettings, updatePrivacySettings } from '../api/privacy';
import { deleteAccount } from '../api/auth';
import { getAccessRequests, approveAccessRequest, denyAccessRequest } from '../api/photos';
import { resetEnrollment, getEnrollmentStatus } from '../api/enrollment';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Settings({ user, onLogout }) {
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [watermarkUrl, setWatermarkUrl] = useState(user?.watermark_url || '');
  const [savingWatermark, setSavingWatermark] = useState(false);
  
  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    face_recognition_enabled: true,
    auto_accept_shares: true,
    allow_strangers: true,
    show_in_search: true
  });
  const [loadingPrivacy, setLoadingPrivacy] = useState(true);
  const [savingPrivacy, setSavingPrivacy] = useState(false);

  // Access requests state
  const [accessRequests, setAccessRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [processingRequest, setProcessingRequest] = useState(null);

  // Re-enrollment state
  const [enrollmentInfo, setEnrollmentInfo] = useState(null);
  const [showReenrollConfirm, setShowReenrollConfirm] = useState(false);
  const [resettingEnrollment, setResettingEnrollment] = useState(false);

  useEffect(() => {
    setWatermarkUrl(user?.watermark_url || '');
    loadPrivacySettings();
    loadAccessRequests();
    loadEnrollmentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadPrivacySettings = async () => {
    try {
      const settings = await getPrivacySettings();
      setPrivacySettings(settings);
    } catch (error) {
    } finally {
      setLoadingPrivacy(false);
    }
  };

  const loadAccessRequests = async () => {
    try {
      const data = await getAccessRequests('pending');
      setAccessRequests(data.requests || []);
    } catch (error) {
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      await approveAccessRequest(requestId);
      toast.success('Access request approved');
      setAccessRequests((prev) => prev.filter((r) => r.request_id !== requestId));
    } catch (error) {
      toast.error(error.message || 'Failed to approve request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleDenyRequest = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      await denyAccessRequest(requestId);
      toast.success('Access request denied');
      setAccessRequests((prev) => prev.filter((r) => r.request_id !== requestId));
    } catch (error) {
      toast.error(error.message || 'Failed to deny request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const loadEnrollmentStatus = async () => {
    try {
      const status = await getEnrollmentStatus();
      setEnrollmentInfo(status);
    } catch (error) {
    }
  };

  const handleReenroll = async () => {
    // First-time enrollment — go straight to onboarding
    if (!enrollmentInfo || enrollmentInfo.embeddings_count === 0) {
      navigate('/onboarding?reenroll=true');
      return;
    }
    // Re-enrollment — reset existing faces first
    setResettingEnrollment(true);
    try {
      await resetEnrollment();
      toast.success('Previous enrollment cleared. Let\'s take new selfies!');
      setShowReenrollConfirm(false);
      navigate('/onboarding?reenroll=true');
    } catch (error) {
      toast.error(error.message || 'Failed to reset enrollment');
    } finally {
      setResettingEnrollment(false);
    }
  };

  const handleLogout = () => {
    onLogout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success('Account deleted successfully');
      onLogout();
      navigate('/login');
    } catch (error) {
      toast.error(error.message || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveWatermarkUrl = async () => {
    setSavingWatermark(true);
    try {
      const response = await fetch(`${API_URL}/api/user/watermark`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ watermark_url: watermarkUrl })
      });

      if (!response.ok) {
        throw new Error('Failed to save');
      }

      toast.success('Watermark link saved!');
    } catch (error) {
      toast.error('Failed to save watermark link');
    } finally {
      setSavingWatermark(false);
    }
  };

  const handlePrivacyToggle = async (key) => {
    const newSettings = {
      ...privacySettings,
      [key]: !privacySettings[key]
    };
    
    setPrivacySettings(newSettings);
    setSavingPrivacy(true);
    
    try {
      await updatePrivacySettings(newSettings);
      toast.success('Privacy settings updated');
    } catch (error) {
      // Revert on error
      setPrivacySettings(privacySettings);
      toast.error('Failed to update privacy settings');
    } finally {
      setSavingPrivacy(false);
    }
  };

  const PrivacyToggle = ({ settingKey, icon: Icon, title, description, warning }) => (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      padding: '1rem 0',
      borderBottom: '1px solid hsl(var(--border))'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        background: privacySettings[settingKey] ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--muted))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Icon size={20} style={{ color: privacySettings[settingKey] ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))' }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>{title}</div>
        <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
          {description}
        </div>
        {warning && !privacySettings[settingKey] && (
          <div style={{ 
            fontSize: '0.75rem', 
            color: 'hsl(var(--destructive))', 
            background: 'hsl(var(--destructive) / 0.1)',
            padding: '0.375rem 0.5rem',
            borderRadius: '6px',
            marginBottom: '0.5rem'
          }}>
            ⚠️ {warning}
          </div>
        )}
      </div>
      <button
        onClick={() => handlePrivacyToggle(settingKey)}
        disabled={savingPrivacy}
        style={{
          width: '52px',
          height: '28px',
          borderRadius: '14px',
          border: 'none',
          background: privacySettings[settingKey] ? 'hsl(var(--primary))' : 'hsl(var(--muted))',
          position: 'relative',
          cursor: 'pointer',
          transition: 'background 0.2s',
          flexShrink: 0
        }}
        data-testid={`toggle-${settingKey}`}
      >
        <div style={{
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          background: 'white',
          position: 'absolute',
          top: '2px',
          left: privacySettings[settingKey] ? '26px' : '2px',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
        }} />
      </button>
    </div>
  );

  return (
    <div className="page" style={{ padding: '1rem', paddingBottom: '100px' }}>
      <div className="container">
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>Settings</h1>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem' }}>
            Manage your account and preferences
          </p>
        </div>

        {/* Account Info */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'hsl(var(--primary) / 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'hsl(var(--primary))',
              fontSize: '1.5rem',
              fontWeight: 600
            }}>
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                {user?.name}
              </h3>
              <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                {user?.email}
              </p>
              <span style={{
                display: 'inline-block',
                marginTop: '0.25rem',
                padding: '0.125rem 0.5rem',
                background: 'hsl(var(--primary) / 0.1)',
                color: 'hsl(var(--primary))',
                borderRadius: '9999px',
                fontSize: '0.7rem',
                fontWeight: 500
              }}>
                {user?.user_type === 'paid' ? '⭐ Paid User' : 'Basic User'}
              </span>
            </div>
          </div>

          {user?.enrollment_complete && (
            <div style={{
              padding: '0.75rem',
              background: 'hsl(var(--success) / 0.1)',
              borderRadius: 'calc(var(--radius) - 4px)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: 'hsl(var(--success))'
            }}>
              <Shield size={16} />
              Face enrollment complete
            </div>
          )}
        </div>

        {/* Face Re-enrollment */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Camera size={18} />
            Face Enrollment
          </h3>
          <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
            Update your face photos for better recognition accuracy
          </p>

          {enrollmentInfo && (
            <div style={{
              padding: '0.75rem',
              background: 'hsl(var(--muted) / 0.3)',
              borderRadius: 'calc(var(--radius) - 4px)',
              marginBottom: '0.75rem',
              fontSize: '0.8rem',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                <span style={{ color: 'hsl(var(--muted-foreground))' }}>Selfies enrolled</span>
                <span style={{ fontWeight: 600 }}>{enrollmentInfo.embeddings_count} / {enrollmentInfo.required_selfies}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'hsl(var(--muted-foreground))' }}>Status</span>
                <span style={{
                  fontWeight: 600,
                  color: enrollmentInfo.enrollment_complete ? 'hsl(var(--success))' : 'hsl(28, 92%, 50%)'
                }}>
                  {enrollmentInfo.enrollment_complete ? 'Complete' : 'Incomplete'}
                </span>
              </div>
            </div>
          )}

          {!showReenrollConfirm ? (
            <button
              onClick={() => {
                if (!enrollmentInfo || enrollmentInfo.embeddings_count === 0) {
                  handleReenroll(); // First-time: go straight to enrollment
                } else {
                  setShowReenrollConfirm(true); // Re-enroll: show confirmation
                }
              }}
              data-testid="reenroll-button"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '0.75rem',
                border: '1px solid hsl(var(--border))',
                background: 'hsl(var(--background))',
                color: 'hsl(var(--foreground))',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'background-color 200ms ease',
              }}
            >
              {enrollmentInfo && enrollmentInfo.embeddings_count > 0 ? (
                <><RefreshCw size={16} /> Re-enroll with New Selfies</>
              ) : (
                <><Camera size={16} /> Enroll Your Face</>
              )}
            </button>
          ) : (
            <div style={{
              padding: '0.75rem',
              borderRadius: '0.75rem',
              border: '1px solid hsl(28, 92%, 50%, 0.3)',
              background: 'hsl(28, 92%, 50%, 0.05)',
            }}>
              <p style={{ fontSize: '0.8rem', color: 'hsl(var(--foreground))', marginBottom: '0.75rem', lineHeight: 1.5 }}>
                This will delete your current face photos and take you through enrollment again. Your existing shared photos won't be affected.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={handleReenroll}
                  disabled={resettingEnrollment}
                  data-testid="confirm-reenroll-button"
                  style={{
                    flex: 1,
                    padding: '0.625rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    background: 'hsl(188, 82%, 35%)',
                    color: 'white',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: resettingEnrollment ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                    transition: 'background-color 200ms ease',
                  }}
                >
                  {resettingEnrollment ? (
                    <div className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
                  ) : (
                    <>
                      <Check size={14} />
                      Yes, Re-enroll
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowReenrollConfirm(false)}
                  data-testid="cancel-reenroll-button"
                  style={{
                    flex: 1,
                    padding: '0.625rem',
                    borderRadius: '0.5rem',
                    border: '1px solid hsl(var(--border))',
                    background: 'transparent',
                    color: 'hsl(var(--muted-foreground))',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'background-color 200ms ease',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>


        {/* Privacy Settings */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Shield size={18} />
            Privacy Settings
          </h3>
          <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
            Control how others can find and share photos with you
          </p>

          {loadingPrivacy ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : (
            <div>
              <PrivacyToggle
                settingKey="face_recognition_enabled"
                icon={Eye}
                title="Face Recognition"
                description="Allow others to find you in their photos using facial recognition"
                warning="You won't appear in others' photos automatically"
              />
              
              <PrivacyToggle
                settingKey="auto_accept_shares"
                icon={Shield}
                title="Auto-Accept Photos"
                description="Automatically add photos to your feed when you're identified"
              />
              
              <PrivacyToggle
                settingKey="allow_strangers"
                icon={Users}
                title="Allow Anyone"
                description="Let anyone share photos with you (not just people you follow)"
              />
              
              <PrivacyToggle
                settingKey="show_in_search"
                icon={Search}
                title="Appear in Search"
                description="Allow others to find you by name or email"
              />
            </div>
          )}
        </div>


        {/* Access Requests Inbox */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Inbox size={18} />
            Access Requests
            {accessRequests.length > 0 && (
              <span style={{
                background: 'hsl(var(--primary))',
                color: 'white',
                fontSize: '0.625rem',
                fontWeight: 600,
                padding: '0.125rem 0.375rem',
                borderRadius: '9999px',
                minWidth: '18px',
                textAlign: 'center',
              }}>
                {accessRequests.length}
              </span>
            )}
          </h3>
          <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
            People detected in the background of your photos can request access
          </p>

          {loadingRequests ? (
            <div style={{ padding: '1.5rem', textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : accessRequests.length === 0 ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem' }} data-testid="no-access-requests">
              No pending requests
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }} data-testid="access-requests-list">
              {accessRequests.map((req) => (
                <div
                  key={req.request_id}
                  data-testid={`access-request-${req.request_id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    borderRadius: '0.75rem',
                    border: '1px solid hsl(var(--border))',
                    background: 'hsl(var(--background))',
                  }}
                >
                  {/* Photo thumbnail */}
                  {req.photo_url && (
                    <img
                      src={req.photo_url}
                      alt="Photo"
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 500, marginBottom: '0.125rem' }}>
                      {req.requester_name}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))' }}>
                      Requests access to your photo
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.375rem', flexShrink: 0 }}>
                    <button
                      onClick={() => handleApproveRequest(req.request_id)}
                      disabled={processingRequest === req.request_id}
                      data-testid={`approve-request-${req.request_id}`}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: 'none',
                        background: 'hsl(162, 72%, 34%, 0.15)',
                        color: 'hsl(162, 72%, 34%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'background-color 200ms ease',
                      }}
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={() => handleDenyRequest(req.request_id)}
                      disabled={processingRequest === req.request_id}
                      data-testid={`deny-request-${req.request_id}`}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: 'none',
                        background: 'hsl(4, 82%, 55%, 0.15)',
                        color: 'hsl(4, 82%, 55%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'background-color 200ms ease',
                      }}
                    >
                      <XIcon size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Watermark Settings - Only for paid users */}
        {user?.user_type === 'paid' && (
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Link size={18} />
              Watermark Link
            </h3>
            <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
              Your name appears on photos you take. Add a link so others can learn more about you.
            </p>
            
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="url"
                value={watermarkUrl}
                onChange={(e) => setWatermarkUrl(e.target.value)}
                placeholder="https://your-website.com"
                style={{
                  flex: 1,
                  padding: '0.625rem 0.75rem',
                  borderRadius: 'var(--radius)',
                  border: '1px solid hsl(var(--border))',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
                data-testid="watermark-url-input"
              />
              <button
                onClick={handleSaveWatermarkUrl}
                disabled={savingWatermark}
                className="btn"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.375rem',
                  background: 'hsl(var(--primary))',
                  color: 'white'
                }}
                data-testid="save-watermark-button"
              >
                <Save size={16} />
                {savingWatermark ? 'Saving...' : 'Save'}
              </button>
            </div>

            {watermarkUrl && (
              <div style={{ 
                marginTop: '0.75rem', 
                padding: '0.5rem', 
                background: 'hsl(var(--muted))', 
                borderRadius: 'var(--radius)',
                fontSize: '0.8rem'
              }}>
                <span style={{ color: 'hsl(var(--muted-foreground))' }}>Preview: </span>
                <a 
                  href={watermarkUrl.startsWith('http') ? watermarkUrl : `https://${watermarkUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: 'hsl(var(--primary))', 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '0.25rem' 
                  }}
                >
                  📷 {user?.name} <ExternalLink size={12} />
                </a>
              </div>
            )}
          </div>
        )}

        {/* Basic user upgrade notice */}
        {user?.user_type === 'basic' && (
          <div className="card" style={{ marginBottom: '1rem', background: 'hsl(var(--primary) / 0.05)' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              ⭐ Upgrade to Paid
            </h3>
            <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
              Paid users get their name displayed on photos they take, with a clickable link to their website.
            </p>
            <button className="btn" style={{ background: 'hsl(var(--primary))', color: 'white' }}>
              Upgrade Now
            </button>
          </div>
        )}

        {/* App Info */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Info size={18} />
            App Information
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'hsl(var(--muted-foreground))' }}>Version</span>
              <span style={{ fontWeight: 500 }}>1.0.0</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'hsl(var(--muted-foreground))' }}>Face Model</span>
              <span style={{ fontWeight: 500 }}>VGG-Face</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'hsl(var(--muted-foreground))' }}>Proximity Radius</span>
              <span style={{ fontWeight: 500 }}>100 meters</span>
            </div>
          </div>
        </div>

        {/* Privacy Note */}
        <div style={{
          padding: '1rem',
          background: 'hsl(var(--brand-mist))',
          borderRadius: 'var(--radius)',
          marginBottom: '1rem',
          fontSize: '0.875rem',
          color: 'hsl(var(--muted-foreground))'
        }}>
          <strong style={{ display: 'block', marginBottom: '0.5rem', color: 'hsl(var(--foreground))' }}>
            Privacy & Security
          </strong>
          Photos are only shared with people detected in them who were nearby when the photo was taken.
          Your location is only used when you're actively using the app.
        </div>

        {/* Logout Button */}
        {!showLogoutConfirm ? (
          <button
            className="btn btn-secondary"
            onClick={() => setShowLogoutConfirm(true)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem' }}
            data-testid="logout-button"
          >
            <LogOut size={18} />
            Logout
          </button>
        ) : (
          <div className="card" style={{ background: 'hsl(var(--destructive) / 0.05)', border: '1px solid hsl(var(--destructive) / 0.2)', marginBottom: '1rem' }}>
            <p style={{ marginBottom: '1rem', fontWeight: 500 }}>Are you sure you want to logout?</p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn"
                onClick={handleLogout}
                style={{ flex: 1, background: 'hsl(var(--destructive))', color: 'white' }}
              >
                Yes, Logout
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => setShowLogoutConfirm(false)}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Delete Account - Danger Zone */}
        <div className="card" style={{ 
          marginBottom: '1rem', 
          border: '1px solid hsl(var(--destructive) / 0.3)',
          background: 'hsl(var(--destructive) / 0.02)'
        }}>
          <h3 style={{ 
            fontSize: '1rem', 
            fontWeight: 600, 
            marginBottom: '0.5rem', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            color: 'hsl(var(--destructive))'
          }}>
            <AlertTriangle size={18} />
            Danger Zone
          </h3>
          
          {!showDeleteConfirm ? (
            <>
              <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="btn"
                style={{ 
                  width: '100%',
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: '0.5rem',
                  background: 'transparent',
                  color: 'hsl(var(--destructive))',
                  border: '1px solid hsl(var(--destructive))'
                }}
                data-testid="delete-account-button"
              >
                <Trash2 size={16} />
                Delete My Account
              </button>
            </>
          ) : (
            <div>
              <div style={{ 
                padding: '1rem', 
                background: 'hsl(var(--destructive) / 0.1)', 
                borderRadius: 'var(--radius)',
                marginBottom: '1rem'
              }}>
                <p style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'hsl(var(--destructive))' }}>
                  ⚠️ This will permanently delete:
                </p>
                <ul style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', paddingLeft: '1.25rem', margin: 0 }}>
                  <li>Your profile and account</li>
                  <li>All photos you've taken</li>
                  <li>All your face enrollment data</li>
                  <li>All messages and shares</li>
                  <li>Your location history</li>
                </ul>
              </div>
              
              <p style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                Type <strong>DELETE</strong> to confirm:
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="Type DELETE"
                style={{
                  width: '100%',
                  padding: '0.625rem 0.75rem',
                  borderRadius: 'var(--radius)',
                  border: '1px solid hsl(var(--destructive) / 0.5)',
                  fontSize: '0.875rem',
                  outline: 'none',
                  marginBottom: '1rem',
                  textAlign: 'center',
                  fontWeight: 600,
                  letterSpacing: '0.1em'
                }}
                data-testid="delete-confirm-input"
              />
              
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                  className="btn btn-ghost"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== 'DELETE' || deleting}
                  className="btn"
                  style={{ 
                    flex: 1, 
                    background: deleteConfirmText === 'DELETE' ? 'hsl(var(--destructive))' : 'hsl(var(--muted))', 
                    color: deleteConfirmText === 'DELETE' ? 'white' : 'hsl(var(--muted-foreground))',
                    cursor: deleteConfirmText === 'DELETE' ? 'pointer' : 'not-allowed'
                  }}
                  data-testid="confirm-delete-account-button"
                >
                  {deleting ? 'Deleting...' : 'Delete Forever'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;
